// Reference for accessing the hooks:
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { Dialog } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSearchParams } from 'react-router-dom';

import { useGetApiV5FirmsRequests } from '@liscio/api';
import { usePersistentFiltersStore, useTrackEvent } from '@liscio/common';
import { Stack, Box, Button, Icon } from '@liscio/ui';

import { RequestsSearchViewSkeleton } from './RequestsSearchViewSkeleton';
import { useEmployeeList } from '../../../custom-hooks/useEmployeeList';
import { BulkSendHeader } from '../components/BulkSendHeader/BulkSendHeader';
import { BulkSendToggler } from '../components/BulkSendToggler/BulkSendToggler';
import { NewRequestPageAlert } from '../components/NewRequestPageAlert/NewRequestPageAlert';
import NewRequestsModal from '../components/NewRequestsModal/NewRequestsModal';
import { RequestsFilterChips } from '../components/RequestsFilterChips/RequestsFilterChips';
import { RequestsSearchInput } from '../components/RequestsSearchInput/RequestsSearchInput';
import { RequestsTable } from '../components/RequestsTable/RequestsTable';
import { RequestsTableFilters } from '../components/RequestsTableFilters/RequestsTableFilters';
import { TotalCountHeader } from '../components/TotalCountHeader/TotalCountHeader';
import { mapFiltersForQuery } from '../helpers';
import { useRequestExperience } from '../hooks/use-experiences-hook';
import { useRequestFilterStore } from '../hooks/useRequestFilterStore';
import { generateRedirectUrl } from 'components/DesktopSiteRedirect/desktop-site-redirect-utils';
import { config } from 'config';
import { useHomeData } from 'fetch-utils/users/user-hooks';

const excludedParameters = [
  'modal',
  'next',
  'new-request-page',
  'company_name', // this comes from the firm.liscio.me redirect
  'redirectedFrom', // this comes from the firm.liscio.me redirect
];

export const RequestsSearchView = () => {
  const { trackEvent } = useTrackEvent();
  const {
    setParametersInSessionStorageAndUrl,
    getParametersFromSessionStorageAndUrl,
  } = usePersistentFiltersStore('requests-list', excludedParameters);
  const [searchParams] = useSearchParams();

  const {
    filters,
    setOwnersList,
    setCurrentUserEmployeeUuid,
    setFilters,
    setRefetchRequests,
    bulkSendModeOn,
  } = useRequestFilterStore();
  const { requestOptInBanners, enableSmartTaxDeliveryFlowRefactor } = useFlags();
  const [uiReady, setUiReady] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const { isNewRequestExperienceEnabled } = useRequestExperience();
  const filtersForQuery = useMemo(() => {
    const mapped = mapFiltersForQuery(filters);
    // When delivery requests are managed in Smart Tax, always exclude tax_delivery from API query
    if (enableSmartTaxDeliveryFlowRefactor) {
      if (mapped.type) {
        mapped.type = (mapped.type as string)
          .split(',')
          .filter((t: string) => t !== 'tax_delivery')
          .join(',') || undefined;
      }
      // If no type filter set (or all types removed), default to non-delivery types
      if (!mapped.type) {
        mapped.type = 'standard,tax_organizer';
      }
    }
    return mapped;
  }, [filters, enableSmartTaxDeliveryFlowRefactor]);

  const {
    data: requestSearchData,
    isLoading,
    fetchStatus,
    refetch: refetchRequests,
  } = useGetApiV5FirmsRequests(filtersForQuery, {
    query: {
      enabled: uiReady,
    },
  });

  useLayoutEffect(() => {
    const redirect = generateRedirectUrl(
      `/requests`,
      `${window.location.search}`,
      window.location.pathname
    );

    async function checkExperience() {
      const isEnabled = await isNewRequestExperienceEnabled();
      if (!isEnabled) {
        window.location.href =
          redirect ?? `${config.legacyRedirect.url}/requests`;
      }
    }

    checkExperience();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestOptInBanners, isNewRequestExperienceEnabled]);

  // update the filters in session storage and url
  useEffect(() => {
    if (uiReady) {
      const newFiltersForQuery = mapFiltersForQuery(filters);
      setParametersInSessionStorageAndUrl(newFiltersForQuery);
    }
  }, [filters, setParametersInSessionStorageAndUrl, uiReady]);

  // set the filters from session storage and url
  useEffect(() => {
    if (!uiReady) {
      const params = getParametersFromSessionStorageAndUrl();
      // When delivery requests are managed in Smart Tax, strip tax_delivery from stored filters
      // (don't set a default here — the query-level useMemo handles that without showing chips)
      if (enableSmartTaxDeliveryFlowRefactor && params.type) {
        const filtered = (params.type as string)
          .split(',')
          .filter((t: string) => t !== 'tax_delivery')
          .join(',');
        if (filtered) {
          params.type = filtered;
        } else {
          delete params.type;
        }
      }
      if (Object.keys(params).length !== 0) {
        setFilters(params);
      }
    }
    // set the ui ready to true
    setUiReady(true);
  }, [enableSmartTaxDeliveryFlowRefactor, getParametersFromSessionStorageAndUrl, setFilters, uiReady]);

  // set the refetch function
  useEffect(() => {
    setRefetchRequests(refetchRequests);
  }, [refetchRequests, setRefetchRequests]);

  // if filtersForQuery changes update the url search params
  // don't put this in the onSettled or cache hits won't trigger it
  useEffect(() => {
    // watch for modal param changes and open appropriate modal
    if (searchParams.get('modal')) {
      if (searchParams.get('modal') === 'newrequest') {
        setRequestModalOpen(true);
      }
    }
  }, [searchParams]);

  // get the home data - this is already cached on loading the app
  const { data: homeData } = useHomeData();

  // get and set the employee list
  const { employeeList, employeeListLoading } = useEmployeeList({
    enabled: uiReady,
  });

  useEffect(() => {
    if (!employeeList.length || employeeListLoading) return;
    setOwnersList(employeeList);
    setCurrentUserEmployeeUuid(homeData?.data?.employee_uuid as string);
  }, [
    employeeList,
    employeeListLoading,
    homeData,
    setCurrentUserEmployeeUuid,
    setOwnersList,
  ]);

  const statusFilters = useMemo(
    () =>
      ((filtersForQuery?.status as string) || '')
        .split(',')
        .filter((itm) => !!itm),
    [filtersForQuery?.status]
  );

  useEffect(() => {
    trackEvent('requestList_view', {
      filters: statusFilters,
    });
  }, [statusFilters, trackEvent]);

  const handleImportButtonClick = () => {
    window.location.href = generateRedirectUrl('/smart-tax');
  };

  return (
    <>
      {requestOptInBanners && <NewRequestPageAlert />}
      <Box
        m={1}
        mt={2}
        display="flex"
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <TotalCountHeader
          isFilteredQueryLoading={isLoading}
          totalFilteredEntries={requestSearchData?.meta?.total_entries}
        />
        <Stack direction="row" justifyContent="space-between" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Icon icon="import" />}
            onClick={handleImportButtonClick}
            sx={{ width: '118px' }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<Icon icon="plus" />}
            onClick={() => setRequestModalOpen(true)}
            sx={{ width: '118px' }}
          >
            Request
          </Button>
        </Stack>
      </Box>
      {/* there's a lot going on during the initial render
      uiReady lets things settle before rendering/setting values
       */}
      {uiReady ? (
        <>
          {bulkSendModeOn ? (
            <BulkSendHeader
              currentUserUUID={homeData?.data?.employee_uuid as string}
            />
          ) : (
            <>
              <Stack direction="row" alignItems="center">
                <BulkSendToggler />
                <RequestsSearchInput />
                <RequestsTableFilters />
              </Stack>
              <Stack direction="row" alignItems="center" mx={2} mb={1}>
                <RequestsFilterChips />
              </Stack>
            </>
          )}
          <RequestsTable
            requestSchema={requestSearchData}
            loading={isLoading && fetchStatus !== 'idle'}
          />
        </>
      ) : (
        <RequestsSearchViewSkeleton />
      )}
      <Dialog maxWidth="md" fullWidth open={requestModalOpen}>
        <NewRequestsModal onClose={() => setRequestModalOpen(false)} />
      </Dialog>
    </>
  );
};
